import { create } from 'zustand'
import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  MarkerType,
  type Connection,
  type EdgeChange,
  type NodeChange,
  type Viewport,
  type XYPosition,
} from '@xyflow/react'
import type {
  AppEdge,
  AppNode,
  DecisionBranchId,
  EdgePathType,
  FlowNodeData,
  NodeKind,
} from '../types'
import { EDGE_DEFAULT_STROKE } from '../types'

const MAX_HISTORY = 100

export type ColorMode = 'light' | 'dark'

export const NODE_DEFAULTS: Record<
  NodeKind,
  { label: string; width: number; height: number; color: string }
> = {
  startEnd: { label: 'Início / Fim', width: 160, height: 60, color: '#22c55e' },
  process: { label: 'Processo', width: 180, height: 70, color: '#3b82f6' },
  decision: { label: 'Decisão?', width: 190, height: 110, color: '#f59e0b' },
  inputOutput: { label: 'Entrada / Saída', width: 200, height: 70, color: '#8b5cf6' },
  annotation: { label: 'Anotação…', width: 220, height: 80, color: '#64748b' },
  group: { label: 'Grupo', width: 440, height: 320, color: '#94a3b8' },
}

/** Padrões Sim/Não nas saídas direita/baixo; top/left começam vazios. */
export const DECISION_BRANCH_DEFAULTS: Partial<Record<DecisionBranchId, string>> = {
  yes: 'Sim',
  no: 'Não',
}

export function resolveDecisionBranchLabel(
  data: FlowNodeData | undefined,
  branchId: DecisionBranchId,
): string {
  const custom = data?.branchLabels?.[branchId]
  if (custom !== undefined) return custom
  return DECISION_BRANCH_DEFAULTS[branchId] ?? ''
}

export const newId = (): string => crypto.randomUUID()

type Snapshot = { nodes: AppNode[]; edges: AppEdge[] }

export type FlowGraphDocument = {
  nodes: AppNode[]
  edges: AppEdge[]
  viewport: Viewport | null
  edgeType?: EdgePathType
  name?: string
}

export function getAbsolutePosition(node: AppNode, nodes: AppNode[]): XYPosition {
  let x = node.position.x
  let y = node.position.y
  let parentId = node.parentId
  while (parentId) {
    const parent = nodes.find((n) => n.id === parentId)
    if (!parent) break
    x += parent.position.x
    y += parent.position.y
    parentId = parent.parentId
  }
  return { x, y }
}

export function collectDescendants(ids: string[], nodes: AppNode[]): Set<string> {
  const result = new Set<string>(ids)
  let changed = true
  while (changed) {
    changed = false
    for (const n of nodes) {
      if (n.parentId && result.has(n.parentId) && !result.has(n.id)) {
        result.add(n.id)
        changed = true
      }
    }
  }
  return result
}

/** Garante que nós pais apareçam antes dos filhos no array (exigência do React Flow). */
export function sortParentsFirst(nodes: AppNode[]): AppNode[] {
  const byId = new Map(nodes.map((n) => [n.id, n]))
  const sorted: AppNode[] = []
  const visited = new Set<string>()
  const visit = (n: AppNode) => {
    if (visited.has(n.id)) return
    visited.add(n.id)
    const parent = n.parentId ? byId.get(n.parentId) : undefined
    if (parent) visit(parent)
    sorted.push(n)
  }
  nodes.forEach(visit)
  return sorted
}

function cloneSelection(
  selNodes: AppNode[],
  selEdges: AppEdge[],
  allNodes: AppNode[],
  offset: number,
): Snapshot {
  const idSet = new Set(selNodes.map((n) => n.id))
  const idMap = new Map<string, string>()
  for (const n of selNodes) idMap.set(n.id, newId())

  const nodes: AppNode[] = selNodes.map((n) => {
    const keepParent = Boolean(n.parentId && idSet.has(n.parentId))
    const abs = getAbsolutePosition(n, allNodes)
    const clone = structuredClone(n)
    return {
      ...clone,
      id: idMap.get(n.id)!,
      parentId: keepParent ? idMap.get(n.parentId!) : undefined,
      position: keepParent
        ? { ...n.position }
        : { x: abs.x + offset, y: abs.y + offset },
      selected: true,
      dragging: false,
    }
  })

  const edges: AppEdge[] = selEdges
    .filter((e) => idSet.has(e.source) && idSet.has(e.target))
    .map((e) => ({
      ...structuredClone(e),
      id: newId(),
      source: idMap.get(e.source)!,
      target: idMap.get(e.target)!,
      selected: true,
    }))

  return { nodes: sortParentsFirst(nodes), edges }
}

export type FlowStore = {
  nodes: AppNode[]
  edges: AppEdge[]
  flowName: string
  edgeType: EdgePathType
  colorMode: ColorMode
  presentationMode: boolean
  readOnly: boolean
  viewport: Viewport | null
  clipboard: Snapshot | null
  past: Snapshot[]
  future: Snapshot[]
  dirty: boolean

  onNodesChange: (changes: NodeChange<AppNode>[]) => void
  onEdgesChange: (changes: EdgeChange<AppEdge>[]) => void
  onConnect: (connection: Connection) => void

  setNodes: (nodes: AppNode[]) => void
  setEdges: (edges: AppEdge[]) => void
  setGraph: (nodes: AppNode[], edges: AppEdge[], snapshot?: boolean) => void

  addNode: (kind: NodeKind, position: XYPosition, parentId?: string) => string
  updateNodeData: (id: string, patch: Partial<FlowNodeData>, snapshot?: boolean) => void
  /** Atualiza rótulo de uma saída da decisão e sincroniza as arestas dessa ponta. */
  setDecisionBranchLabel: (
    nodeId: string,
    branchId: DecisionBranchId,
    label: string,
  ) => void
  updateEdge: (id: string, patch: Partial<AppEdge>, snapshot?: boolean) => void
  deleteNodes: (ids: string[]) => void
  duplicateNodes: (ids: string[]) => void

  copySelection: () => void
  pasteClipboard: () => void

  takeSnapshot: () => void
  undo: () => void
  redo: () => void

  setFlowName: (name: string) => void
  setEdgeType: (type: EdgePathType) => void
  setColorMode: (mode: ColorMode) => void
  setPresentationMode: (value: boolean) => void
  setReadOnly: (value: boolean) => void
  togglePresentationMode: () => void
  setViewportState: (viewport: Viewport) => void
  clearAll: () => void
  loadGraph: (doc: FlowGraphDocument) => void
  getGraphForSave: () => FlowGraphDocument
  markClean: () => void
  resetStore: () => void
}

const emptyState = {
  nodes: [] as AppNode[],
  edges: [] as AppEdge[],
  flowName: 'Novo fluxograma',
  edgeType: 'smoothstep' as EdgePathType,
  colorMode: 'light' as ColorMode,
  presentationMode: false,
  readOnly: false,
  viewport: null as Viewport | null,
  clipboard: null as Snapshot | null,
  past: [] as Snapshot[],
  future: [] as Snapshot[],
  dirty: false,
}

export const useFlowStore = create<FlowStore>((set, get) => ({
  ...emptyState,

  onNodesChange: (changes) => {
    const locked = get().readOnly || get().presentationMode
    if (locked) {
      const selectionOnly = changes.every(
        (c) => c.type === 'select' || c.type === 'dimensions'
      )
      if (!selectionOnly) return
      set({ nodes: applyNodeChanges(changes, get().nodes) })
      return
    }
    const metaOnly = changes.every(
      (c) => c.type === 'select' || c.type === 'dimensions'
    )
    set({
      nodes: applyNodeChanges(changes, get().nodes),
      dirty: metaOnly ? get().dirty : true,
    })
  },

  onEdgesChange: (changes) => {
    const locked = get().readOnly || get().presentationMode
    if (locked) {
      const selectionOnly = changes.every((c) => c.type === 'select')
      if (!selectionOnly) return
      set({ edges: applyEdgeChanges(changes, get().edges) })
      return
    }
    const metaOnly = changes.every((c) => c.type === 'select')
    set({
      edges: applyEdgeChanges(changes, get().edges),
      dirty: metaOnly ? get().dirty : true,
    })
  },

  onConnect: (connection) => {
    if (get().readOnly || get().presentationMode) return
    const { nodes, edges, edgeType, takeSnapshot } = get()
    takeSnapshot()
    const sourceNode = nodes.find((n) => n.id === connection.source)
    let label = ''
    if (sourceNode?.type === 'decision' && connection.sourceHandle) {
      label = resolveDecisionBranchLabel(
        sourceNode.data,
        connection.sourceHandle as DecisionBranchId,
      )
    }
    const edge: AppEdge = {
      ...connection,
      id: newId(),
      type: 'labeled',
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: EDGE_DEFAULT_STROKE,
      },
      style: { stroke: EDGE_DEFAULT_STROKE, strokeWidth: 2 },
      data: { label, pathType: edgeType },
    }
    set({ edges: addEdge(edge, edges), dirty: true })
  },

  setNodes: (nodes) => set({ nodes, dirty: true }),
  setEdges: (edges) => set({ edges, dirty: true }),

  setGraph: (nodes, edges, snapshot = true) => {
    if (snapshot) get().takeSnapshot()
    set({ nodes: sortParentsFirst(nodes), edges, dirty: true })
  },

  addNode: (kind, position, parentId) => {
    if (get().readOnly || get().presentationMode) return ''
    const { takeSnapshot, nodes } = get()
    takeSnapshot()
    const defaults = NODE_DEFAULTS[kind]
    const id = newId()
    const node: AppNode = {
      id,
      type: kind,
      position,
      parentId,
      width: defaults.width,
      height: defaults.height,
      data: { label: defaults.label, color: defaults.color },
      selected: true,
    }
    const deselected = nodes.map((n) => (n.selected ? { ...n, selected: false } : n))
    const next =
      kind === 'group' ? [node, ...deselected] : [...deselected, node]
    set({ nodes: sortParentsFirst(next), dirty: true })
    return id
  },

  updateNodeData: (id, patch, snapshot = true) => {
    if (get().readOnly || get().presentationMode) return
    if (snapshot) get().takeSnapshot()
    set({
      nodes: get().nodes.map((n) =>
        n.id === id ? { ...n, data: { ...n.data, ...patch } } : n,
      ),
      dirty: true,
    })
  },

  setDecisionBranchLabel: (nodeId, branchId, label) => {
    if (get().readOnly || get().presentationMode) return
    get().takeSnapshot()
    const { nodes, edges } = get()
    set({
      nodes: nodes.map((n) =>
        n.id === nodeId
          ? {
              ...n,
              data: {
                ...n.data,
                branchLabels: { ...n.data.branchLabels, [branchId]: label },
              },
            }
          : n,
      ),
      edges: edges.map((e) =>
        e.source === nodeId && e.sourceHandle === branchId
          ? { ...e, data: { ...e.data, label } }
          : e,
      ),
      dirty: true,
    })
  },

  updateEdge: (id, patch, snapshot = true) => {
    if (get().readOnly || get().presentationMode) return
    if (snapshot) get().takeSnapshot()
    set({
      edges: get().edges.map((e) =>
        e.id === id
          ? { ...e, ...patch, data: { ...e.data, ...patch.data } }
          : e,
      ),
      dirty: true,
    })
  },

  deleteNodes: (ids) => {
    if (get().readOnly || get().presentationMode) return
    const { nodes, edges, takeSnapshot } = get()
    if (ids.length === 0) return
    takeSnapshot()
    const toRemove = collectDescendants(ids, nodes)
    set({
      nodes: nodes.filter((n) => !toRemove.has(n.id)),
      edges: edges.filter(
        (e) => !toRemove.has(e.source) && !toRemove.has(e.target),
      ),
      dirty: true,
    })
  },

  duplicateNodes: (ids) => {
    if (get().readOnly || get().presentationMode) return
    const { nodes, edges, takeSnapshot } = get()
    if (ids.length === 0) return
    takeSnapshot()
    const fullSet = collectDescendants(ids, nodes)
    const selNodes = nodes.filter((n) => fullSet.has(n.id))
    const clone = cloneSelection(selNodes, edges, nodes, 40)
    set({
      nodes: [
        ...nodes.map((n) => (n.selected ? { ...n, selected: false } : n)),
        ...clone.nodes,
      ],
      edges: [
        ...edges.map((e) => (e.selected ? { ...e, selected: false } : e)),
        ...clone.edges,
      ],
      dirty: true,
    })
  },

  copySelection: () => {
    const { nodes, edges } = get()
    const selectedIds = nodes.filter((n) => n.selected).map((n) => n.id)
    if (selectedIds.length === 0) return
    const fullSet = collectDescendants(selectedIds, nodes)
    const selNodes = nodes.filter((n) => fullSet.has(n.id))
    const selEdges = edges.filter(
      (e) => fullSet.has(e.source) && fullSet.has(e.target),
    )
    set({ clipboard: cloneSelection(selNodes, selEdges, nodes, 0) })
  },

  pasteClipboard: () => {
    if (get().readOnly || get().presentationMode) return
    const { clipboard, nodes, edges, takeSnapshot } = get()
    if (!clipboard) return
    takeSnapshot()
    const clone = cloneSelection(
      clipboard.nodes,
      clipboard.edges,
      clipboard.nodes,
      40,
    )
    set({
      nodes: [
        ...nodes.map((n) => (n.selected ? { ...n, selected: false } : n)),
        ...clone.nodes,
      ],
      edges: [
        ...edges.map((e) => (e.selected ? { ...e, selected: false } : e)),
        ...clone.edges,
      ],
      dirty: true,
    })
  },

  takeSnapshot: () => {
    const { nodes, edges, past } = get()
    set({
      past: [...past.slice(-(MAX_HISTORY - 1)), { nodes, edges }],
      future: [],
    })
  },

  undo: () => {
    if (get().readOnly) return
    const { past, future, nodes, edges } = get()
    const previous = past[past.length - 1]
    if (!previous) return
    set({
      past: past.slice(0, -1),
      future: [{ nodes, edges }, ...future],
      nodes: previous.nodes,
      edges: previous.edges,
      dirty: true,
    })
  },

  redo: () => {
    if (get().readOnly) return
    const { past, future, nodes, edges } = get()
    const next = future[0]
    if (!next) return
    set({
      past: [...past, { nodes, edges }],
      future: future.slice(1),
      nodes: next.nodes,
      edges: next.edges,
      dirty: true,
    })
  },

  setFlowName: (flowName) => set({ flowName, dirty: true }),
  setEdgeType: (edgeType) => set({ edgeType, dirty: true }),
  setColorMode: (colorMode) => set({ colorMode }),
  setPresentationMode: (presentationMode) =>
    set({
      presentationMode,
      nodes: get().nodes.map((n) => (n.selected ? { ...n, selected: false } : n)),
      edges: get().edges.map((e) => (e.selected ? { ...e, selected: false } : e)),
    }),
  setReadOnly: (readOnly) =>
    set({
      readOnly,
      presentationMode: readOnly ? true : get().presentationMode,
    }),
  togglePresentationMode: () => {
    if (get().readOnly) return
    set({
      presentationMode: !get().presentationMode,
      nodes: get().nodes.map((n) => (n.selected ? { ...n, selected: false } : n)),
      edges: get().edges.map((e) => (e.selected ? { ...e, selected: false } : e)),
    })
  },
  setViewportState: (viewport) => {
    if (get().readOnly || get().presentationMode) {
      set({ viewport })
      return
    }
    set({ viewport, dirty: true })
  },

  clearAll: () => {
    if (get().readOnly || get().presentationMode) return
    get().takeSnapshot()
    set({ nodes: [], edges: [], dirty: true })
  },

  loadGraph: (doc) => {
    set({
      nodes: sortParentsFirst((doc.nodes || []) as AppNode[]),
      edges: (doc.edges || []) as AppEdge[],
      viewport: doc.viewport ?? null,
      edgeType: doc.edgeType ?? 'smoothstep',
      flowName: doc.name ?? get().flowName,
      past: [],
      future: [],
      dirty: false,
      clipboard: null,
    })
  },

  getGraphForSave: () => {
    const { nodes, edges, viewport, edgeType, flowName } = get()
    return {
      nodes,
      edges,
      viewport,
      edgeType,
      name: flowName,
    }
  },

  markClean: () => set({ dirty: false }),

  resetStore: () => set({ ...emptyState }),
}))
